/**
 * Early Reading Curriculum Data (Kurikulum Belajar Membaca TK A)
 * Structured for progressive literacy:
 * Huruf -> Bunyi -> Suku Kata -> Kata -> Kalimat
 */

export interface LetterItem {
  id: string;
  letter: string;
  lowercase: string;
  phonics: string;
  exampleWord: string;
  exampleSyllables: string[];
  emoji: string;
  color: string;
}

export interface SyllableItem {
  syllable: string;
  vowel: string;
  exampleWord?: string;
  emoji: string;
  spokenSyllable?: string;
}

export interface SyllableFamily {
  consonant: string;
  items: SyllableItem[];
}

export interface WordItem {
  id: string;
  word: string;
  syllables: string[];
  emoji: string;
  category: string;
  sentenceExample: string;
}

export const ALPHABET_DATA: LetterItem[] = [
  { id: 'letter-a', letter: 'A', lowercase: 'a', phonics: 'a', exampleWord: 'Apel', exampleSyllables: ['a', 'pel'], emoji: '🍎', color: '#EF4444' },
  { id: 'letter-b', letter: 'B', lowercase: 'b', phonics: 'beh', exampleWord: 'Bola', exampleSyllables: ['bo', 'la'], emoji: '⚽', color: '#3B82F6' },
  { id: 'letter-c', letter: 'C', lowercase: 'c', phonics: 'ceh', exampleWord: 'Ceri', exampleSyllables: ['ce', 'ri'], emoji: '🍒', color: '#EC4899' },
  { id: 'letter-d', letter: 'D', lowercase: 'd', phonics: 'deh', exampleWord: 'Daun', exampleSyllables: ['da', 'un'], emoji: '🍃', color: '#10B981' },
  { id: 'letter-e', letter: 'E', lowercase: 'e', phonics: 'eh', exampleWord: 'Emas', exampleSyllables: ['e', 'mas'], emoji: '⭐', color: '#F59E0B' },
  { id: 'letter-f', letter: 'F', lowercase: 'f', phonics: 'ef', exampleWord: 'Foto', exampleSyllables: ['fo', 'to'], emoji: '📷', color: '#8B5CF6' },
  { id: 'letter-g', letter: 'G', lowercase: 'g', phonics: 'geh', exampleWord: 'Gajah', exampleSyllables: ['ga', 'jah'], emoji: '🐘', color: '#6366F1' },
  { id: 'letter-h', letter: 'H', lowercase: 'h', phonics: 'hah', exampleWord: 'Hati', exampleSyllables: ['ha', 'ti'], emoji: '❤️', color: '#F43F5E' },
  { id: 'letter-i', letter: 'I', lowercase: 'i', phonics: 'i', exampleWord: 'Ikan', exampleSyllables: ['i', 'kan'], emoji: '🐟', color: '#06B6D4' },
  { id: 'letter-j', letter: 'J', lowercase: 'j', phonics: 'jeh', exampleWord: 'Jam', exampleSyllables: ['jam'], emoji: '⏰', color: '#D97706' },
  { id: 'letter-k', letter: 'K', lowercase: 'k', phonics: 'kah', exampleWord: 'Kuda', exampleSyllables: ['ku', 'da'], emoji: '🐴', color: '#84CC16' },
  { id: 'letter-l', letter: 'L', lowercase: 'l', phonics: 'el', exampleWord: 'Lampu', exampleSyllables: ['lam', 'pu'], emoji: '💡', color: '#EAB308' },
  { id: 'letter-m', letter: 'M', lowercase: 'm', phonics: 'em', exampleWord: 'Mata', exampleSyllables: ['ma', 'ta'], emoji: '👀', color: '#14B8A6' },
  { id: 'letter-n', letter: 'N', lowercase: 'n', phonics: 'en', exampleWord: 'Nasi', exampleSyllables: ['na', 'si'], emoji: '🍚', color: '#64748B' },
  { id: 'letter-o', letter: 'O', lowercase: 'o', phonics: 'o', exampleWord: 'Obor', exampleSyllables: ['o', 'bor'], emoji: '🔥', color: '#F97316' },
  { id: 'letter-p', letter: 'P', lowercase: 'p', phonics: 'peh', exampleWord: 'Pisang', exampleSyllables: ['pi', 'sang'], emoji: '🍌', color: '#FBBF24' },
  { id: 'letter-q', letter: 'Q', lowercase: 'q', phonics: 'ki', exampleWord: 'Quran', exampleSyllables: ['qur', 'an'], emoji: '📖', color: '#059669' },
  { id: 'letter-r', letter: 'R', lowercase: 'r', phonics: 'er', exampleWord: 'Roti', exampleSyllables: ['ro', 'ti'], emoji: '🍞', color: '#B45309' },
  { id: 'letter-s', letter: 'S', lowercase: 's', phonics: 'es', exampleWord: 'Susu', exampleSyllables: ['su', 'su'], emoji: '🥛', color: '#38BDF8' },
  { id: 'letter-t', letter: 'T', lowercase: 't', phonics: 'teh', exampleWord: 'Topi', exampleSyllables: ['to', 'pi'], emoji: '🧢', color: '#4F46E5' },
  { id: 'letter-u', letter: 'U', lowercase: 'u', phonics: 'u', exampleWord: 'Udang', exampleSyllables: ['u', 'dang'], emoji: '🦐', color: '#FB7185' },
  { id: 'letter-v', letter: 'V', lowercase: 'v', phonics: 've', exampleWord: 'Vas', exampleSyllables: ['vas'], emoji: '🏺', color: '#7C3AED' },
  { id: 'letter-w', letter: 'W', lowercase: 'w', phonics: 'we', exampleWord: 'Wortel', exampleSyllables: ['wor', 'tel'], emoji: '🥕', color: '#EA580C' },
  { id: 'letter-x', letter: 'X', lowercase: 'x', phonics: 'eks', exampleWord: 'Xilofon', exampleSyllables: ['xi', 'lo', 'fon'], emoji: '🎵', color: '#9333EA' },
  { id: 'letter-y', letter: 'Y', lowercase: 'y', phonics: 'ye', exampleWord: 'Yoyo', exampleSyllables: ['yo', 'yo'], emoji: '🪀', color: '#0284C7' },
  { id: 'letter-z', letter: 'Z', lowercase: 'z', phonics: 'zet', exampleWord: 'Zebra', exampleSyllables: ['ze', 'bra'], emoji: '🦓', color: '#475569' },
];

export const SYLLABLE_FAMILIES: SyllableFamily[] = [
  {
    consonant: 'B',
    items: [
      { syllable: 'BA', vowel: 'A', exampleWord: 'Baju', emoji: '👕' },
      { syllable: 'BI', vowel: 'I', exampleWord: 'Bibir', emoji: '👄' },
      { syllable: 'BU', vowel: 'U', exampleWord: 'Buku', emoji: '📚' },
      { syllable: 'BE', vowel: 'E', exampleWord: 'Bebek', emoji: '🦆', spokenSyllable: 'beh' },
      { syllable: 'BO', vowel: 'O', exampleWord: 'Bola', emoji: '⚽' },
    ],
  },
  {
    consonant: 'C',
    items: [
      { syllable: 'CA', vowel: 'A', exampleWord: 'Cacing', emoji: '🪱', spokenSyllable: 'cha' },
      { syllable: 'CI', vowel: 'I', exampleWord: 'Cincin', emoji: '💍' },
      { syllable: 'CU', vowel: 'U', exampleWord: 'Cumi', emoji: '🦑' },
      { syllable: 'CE', vowel: 'E', exampleWord: 'Ceri', emoji: '🍒' },
      { syllable: 'CO', vowel: 'O', exampleWord: 'Cokelat', emoji: '🍫', spokenSyllable: 'cho' },
    ],
  },
  {
    consonant: 'D',
    items: [
      { syllable: 'DA', vowel: 'A', exampleWord: 'Daun', emoji: '🍃' },
      { syllable: 'DI', vowel: 'I', exampleWord: 'Dinding', emoji: '🧱' },
      { syllable: 'DU', vowel: 'U', exampleWord: 'Duku', emoji: '🍈' },
      { syllable: 'DE', vowel: 'E', exampleWord: 'Delapan', emoji: '8️⃣' },
      { syllable: 'DO', vowel: 'O', exampleWord: 'Domba', emoji: '🐑' },
    ],
  },
  {
    consonant: 'F',
    items: [
      { syllable: 'FA', vowel: 'A', exampleWord: 'Fajar', emoji: '🌅' },
      { syllable: 'FI', vowel: 'I', exampleWord: 'Film', emoji: '🎬' },
      { syllable: 'FU', vowel: 'U', exampleWord: 'Futsal', emoji: '⚽' },
      { syllable: 'FE', vowel: 'E', exampleWord: 'Festival', emoji: '🎉' },
      { syllable: 'FO', vowel: 'O', exampleWord: 'Foto', emoji: '📷' },
    ],
  },
  {
    consonant: 'G',
    items: [
      { syllable: 'GA', vowel: 'A', exampleWord: 'Gajah', emoji: '🐘' },
      { syllable: 'GI', vowel: 'I', exampleWord: 'Gigi', emoji: '🦷' },
      { syllable: 'GU', vowel: 'U', exampleWord: 'Gula', emoji: '🍬' },
      { syllable: 'GE', vowel: 'E', exampleWord: 'Gelas', emoji: '🥛' },
      { syllable: 'GO', vowel: 'O', exampleWord: 'Gitar', emoji: '🎸' },
    ],
  },
  {
    consonant: 'H',
    items: [
      { syllable: 'HA', vowel: 'A', exampleWord: 'Hati', emoji: '❤️' },
      { syllable: 'HI', vowel: 'I', exampleWord: 'Hitam', emoji: '⬛', spokenSyllable: 'hii' },
      { syllable: 'HU', vowel: 'U', exampleWord: 'Hujan', emoji: '🌧️' },
      { syllable: 'HE', vowel: 'E', exampleWord: 'Helai', emoji: '🧵' },
      { syllable: 'HO', vowel: 'O', exampleWord: 'Hotel', emoji: '🏨' },
    ],
  },
  {
    consonant: 'J',
    items: [
      { syllable: 'JA', vowel: 'A', exampleWord: 'Jalan', emoji: '🛣️' },
      { syllable: 'JI', vowel: 'I', exampleWord: 'Jintan', emoji: '🌿' },
      { syllable: 'JU', vowel: 'U', exampleWord: 'Jus', emoji: '🧃' },
      { syllable: 'JE', vowel: 'E', exampleWord: 'Jeruk', emoji: '🍊' },
      { syllable: 'JO', vowel: 'O', exampleWord: 'Joran', emoji: '🎣' },
    ],
  },
  {
    consonant: 'K',
    items: [
      { syllable: 'KA', vowel: 'A', exampleWord: 'Kaki', emoji: '🦶' },
      { syllable: 'KI', vowel: 'I', exampleWord: 'Kipas', emoji: '🪭' },
      { syllable: 'KU', vowel: 'U', exampleWord: 'Kuda', emoji: '🐴' },
      { syllable: 'KE', vowel: 'E', exampleWord: 'Kelapa', emoji: '🥥' },
      { syllable: 'KO', vowel: 'O', exampleWord: 'Kopi', emoji: '☕' },
    ],
  },
  {
    consonant: 'L',
    items: [
      { syllable: 'LA', vowel: 'A', exampleWord: 'Lampu', emoji: '💡' },
      { syllable: 'LI', vowel: 'I', exampleWord: 'Lilin', emoji: '🕯️' },
      { syllable: 'LU', vowel: 'U', exampleWord: 'Lumba', emoji: '🐬' },
      { syllable: 'LE', vowel: 'E', exampleWord: 'Lemon', emoji: '🍋' },
      { syllable: 'LO', vowel: 'O', exampleWord: 'Lobak', emoji: '🥕' },
    ],
  },
  {
    consonant: 'M',
    items: [
      { syllable: 'MA', vowel: 'A', exampleWord: 'Mata', emoji: '👀' },
      { syllable: 'MI', vowel: 'I', exampleWord: 'Minum', emoji: '🥤' },
      { syllable: 'MU', vowel: 'U', exampleWord: 'Mulut', emoji: '👄' },
      { syllable: 'ME', vowel: 'E', exampleWord: 'Melon', emoji: '🍈', spokenSyllable: 'mei' },
      { syllable: 'MO', vowel: 'O', exampleWord: 'Mobil', emoji: '🚗' },
    ],
  },
  {
    consonant: 'N',
    items: [
      { syllable: 'NA', vowel: 'A', exampleWord: 'Nasi', emoji: '🍚' },
      { syllable: 'NI', vowel: 'I', exampleWord: 'Nila', emoji: '🐟' },
      { syllable: 'NU', vowel: 'U', exampleWord: 'Nusa', emoji: '🏝️' },
      { syllable: 'NE', vowel: 'E', exampleWord: 'Nenek', emoji: '👵' },
      { syllable: 'NO', vowel: 'O', exampleWord: 'Noda', emoji: '🔵' },
    ],
  },
  {
    consonant: 'P',
    items: [
      { syllable: 'PA', vowel: 'A', exampleWord: 'Padi', emoji: '🌾' },
      { syllable: 'PI', vowel: 'I', exampleWord: 'Pintu', emoji: '🚪' },
      { syllable: 'PU', vowel: 'U', exampleWord: 'Pohon', emoji: '🌳' },
      { syllable: 'PE', vowel: 'E', exampleWord: 'Pena', emoji: '🖊️' },
      { syllable: 'PO', vowel: 'O', exampleWord: 'Polisi', emoji: '👮' },
    ],
  },
  {
    consonant: 'Q',
    items: [
      { syllable: 'QA', vowel: 'A', exampleWord: 'Qari', emoji: '📖', spokenSyllable: 'ka' },
      { syllable: 'QI', vowel: 'I', exampleWord: 'Qiraah', emoji: '🕌', spokenSyllable: 'ki' },
      { syllable: 'QU', vowel: 'U', exampleWord: 'Qurban', emoji: '🐑', spokenSyllable: 'ku' },
      { syllable: 'QE', vowel: 'E', emoji: '🔤', spokenSyllable: 'ke' },
      { syllable: 'QO', vowel: 'O', emoji: '🔤', spokenSyllable: 'ko' },
    ],
  },
  {
    consonant: 'R',
    items: [
      { syllable: 'RA', vowel: 'A', exampleWord: 'Rasa', emoji: '😋' },
      { syllable: 'RI', vowel: 'I', exampleWord: 'Ribu', emoji: '🔢' },
      { syllable: 'RU', vowel: 'U', exampleWord: 'Rumah', emoji: '🏠' },
      { syllable: 'RE', vowel: 'E', exampleWord: 'Rembulan', emoji: '🌙' },
      { syllable: 'RO', vowel: 'O', exampleWord: 'Roti', emoji: '🍞' },
    ],
  },
  {
    consonant: 'S',
    items: [
      { syllable: 'SA', vowel: 'A', exampleWord: 'Sapi', emoji: '🐮' },
      { syllable: 'SI', vowel: 'I', exampleWord: 'Singa', emoji: '🦁' },
      { syllable: 'SU', vowel: 'U', exampleWord: 'Susu', emoji: '🥛' },
      { syllable: 'SE', vowel: 'E', exampleWord: 'Sepeda', emoji: '🚲' },
      { syllable: 'SO', vowel: 'O', exampleWord: 'Soto', emoji: '🍲' },
    ],
  },
  {
    consonant: 'T',
    items: [
      { syllable: 'TA', vowel: 'A', exampleWord: 'Tali', emoji: '🪢' },
      { syllable: 'TI', vowel: 'I', exampleWord: 'Tidur', emoji: '😴' },
      { syllable: 'TU', vowel: 'U', exampleWord: 'Tupai', emoji: '🐿️' },
      { syllable: 'TE', vowel: 'E', exampleWord: 'Telur', emoji: '🥚' },
      { syllable: 'TO', vowel: 'O', exampleWord: 'Topi', emoji: '🧢', spokenSyllable: 'tow' },
    ],
  },
  {
    consonant: 'V',
    items: [
      { syllable: 'VA', vowel: 'A', exampleWord: 'Vas', emoji: '🏺' },
      { syllable: 'VI', vowel: 'I', exampleWord: 'Video', emoji: '🎥' },
      { syllable: 'VU', vowel: 'U', exampleWord: 'Vulkan', emoji: '🌋' },
      { syllable: 'VE', vowel: 'E', exampleWord: 'Vespa', emoji: '🛵' },
      { syllable: 'VO', vowel: 'O', exampleWord: 'Vokal', emoji: '🎤' },
    ],
  },
  {
    consonant: 'W',
    items: [
      { syllable: 'WA', vowel: 'A', exampleWord: 'Wajah', emoji: '🙂' },
      { syllable: 'WI', vowel: 'I', exampleWord: 'Wira', emoji: '🧑' },
      { syllable: 'WU', vowel: 'U', exampleWord: 'Wujud', emoji: '✨' },
      { syllable: 'WE', vowel: 'E', exampleWord: 'Wesel', emoji: '✉️', spokenSyllable: 'weh' },
      { syllable: 'WO', vowel: 'O', exampleWord: 'Wortel', emoji: '🥕' },
    ],
  },
  {
    consonant: 'X',
    items: [
      { syllable: 'XA', vowel: 'A', emoji: '🔤', spokenSyllable: 'ksa' },
      { syllable: 'XI', vowel: 'I', exampleWord: 'Xilofon', emoji: '🎵', spokenSyllable: 'ksi' },
      { syllable: 'XU', vowel: 'U', emoji: '🔤', spokenSyllable: 'ksu' },
      { syllable: 'XE', vowel: 'E', exampleWord: 'Xenon', emoji: '💡', spokenSyllable: 'kse' },
      { syllable: 'XO', vowel: 'O', emoji: '🔤', spokenSyllable: 'kso' },
    ],
  },
  {
    consonant: 'Y',
    items: [
      { syllable: 'YA', vowel: 'A', exampleWord: 'Yakin', emoji: '👍' },
      { syllable: 'YI', vowel: 'I', exampleWord: 'Yin', emoji: '☯️' },
      { syllable: 'YU', vowel: 'U', exampleWord: 'Yuyu', emoji: '🐚' },
      { syllable: 'YE', vowel: 'E', exampleWord: 'Yes', emoji: '👍' },
      { syllable: 'YO', vowel: 'O', exampleWord: 'Yoyo', emoji: '🪀' },
    ],
  },
  {
    consonant: 'Z',
    items: [
      { syllable: 'ZA', vowel: 'A', exampleWord: 'Zaitun', emoji: '🫒' },
      { syllable: 'ZI', vowel: 'I', exampleWord: 'Zikir', emoji: '📿' },
      { syllable: 'ZU', vowel: 'U', exampleWord: 'Zuhur', emoji: '🕌' },
      { syllable: 'ZE', vowel: 'E', exampleWord: 'Zebra', emoji: '🦓' },
      { syllable: 'ZO', vowel: 'O', exampleWord: 'Zona', emoji: '🗺️' },
    ],
  },
];

export const WORD_BUILDER_ITEMS: WordItem[] = [
  { id: 'w-baju', word: 'BAJU', syllables: ['BA', 'JU'], emoji: '👕', category: 'Pakaian', sentenceExample: 'Ini baju baru.' },
  { id: 'w-bola', word: 'BOLA', syllables: ['BO', 'LA'], emoji: '⚽', category: 'Mainan', sentenceExample: 'Adik main bola.' },
  { id: 'w-buku', word: 'BUKU', syllables: ['BU', 'KU'], emoji: '📚', category: 'Sekolah', sentenceExample: 'Ayah baca buku.' },
  { id: 'w-melon', word: 'MELON', syllables: ['ME', 'LON'], emoji: '🍈', category: 'Makanan', sentenceExample: 'Mila makan melon.' },
  { id: 'w-roti', word: 'ROTI', syllables: ['RO', 'TI'], emoji: '🍞', category: 'Makanan', sentenceExample: 'Ibu makan roti.' },
  { id: 'w-susu', word: 'SUSU', syllables: ['SU', 'SU'], emoji: '🥛', category: 'Minuman', sentenceExample: 'Mila minum susu.' },
  { id: 'w-topi', word: 'TOPI', syllables: ['TO', 'PI'], emoji: '🧢', category: 'Pakaian', sentenceExample: 'Budi pakai topi.' },
  { id: 'w-kuda', word: 'KUDA', syllables: ['KU', 'DA'], emoji: '🐴', category: 'Hewan', sentenceExample: 'Kuda lari kencang.' },
  { id: 'w-sapi', word: 'SAPI', syllables: ['SA', 'PI'], emoji: '🐄', category: 'Hewan', sentenceExample: 'Sapi makan rumput.' },
  { id: 'w-kaki', word: 'KAKI', syllables: ['KA', 'KI'], emoji: '🦶', category: 'Tubuh', sentenceExample: 'Kaki untuk berjalan.' },
  { id: 'w-mata', word: 'MATA', syllables: ['MA', 'TA'], emoji: '👀', category: 'Tubuh', sentenceExample: 'Mata untuk melihat.' },
  { id: 'w-sepeda', word: 'SEPEDA', syllables: ['SE', 'PE', 'DA'], emoji: '🚲', category: 'Kendaraan', sentenceExample: 'Ayo naik sepeda.' },
  { id: 'w-hati', word: 'HATI', syllables: ['HA', 'TI'], emoji: '❤️', category: 'Tubuh', sentenceExample: 'Hati perlu dijaga.' },
  { id: 'w-nasi', word: 'NASI', syllables: ['NA', 'SI'], emoji: '🍚', category: 'Makanan', sentenceExample: 'Mila makan nasi.' },
  { id: 'w-rasa', word: 'RASA', syllables: ['RA', 'SA'], emoji: '😋', category: 'Makanan', sentenceExample: 'Rasa jus itu manis.' },
  { id: 'w-foto', word: 'FOTO', syllables: ['FO', 'TO'], emoji: '📷', category: 'Benda', sentenceExample: 'Ini foto keluarga.' },
  { id: 'w-gigi', word: 'GIGI', syllables: ['GI', 'GI'], emoji: '🦷', category: 'Tubuh', sentenceExample: 'Ayo sikat gigi.' },
  { id: 'w-dasi', word: 'DASI', syllables: ['DA', 'SI'], emoji: '👔', category: 'Pakaian', sentenceExample: 'Ayah memakai dasi.' },
  { id: 'w-cincin', word: 'CINCIN', syllables: ['CIN', 'CIN'], emoji: '💍', category: 'Benda', sentenceExample: 'Cincin itu berkilau.' },
  { id: 'w-zebra', word: 'ZEBRA', syllables: ['ZE', 'BRA'], emoji: '🦓', category: 'Hewan', sentenceExample: 'Zebra punya belang.' },
  { id: 'w-yoyo', word: 'YOYO', syllables: ['YO', 'YO'], emoji: '🪀', category: 'Mainan', sentenceExample: 'Yoyo berputar turun.' },
  { id: 'w-wortel', word: 'WORTEL', syllables: ['WOR', 'TEL'], emoji: '🥕', category: 'Makanan', sentenceExample: 'Wortel baik untuk tubuh.' },
];

export const SAMPLE_REAL_WORLD_SIGNS = [
  {
    title: 'Rambu Keselamatan',
    text: 'HATI-HATI DI JALAN',
    context: 'Rambu Jalan Raya',
    category: 'Rambu Publik',
    imageUrl: 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Papan Nama Toko',
    text: 'TOKO BUKU KITA',
    context: 'Toko Buku Sekolah',
    category: 'Pertokoan',
    imageUrl: 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Spanduk Warung',
    text: 'BAKSO SAPI ENAK',
    context: 'Kedai Makanan',
    category: 'Makanan',
    imageUrl: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Tanda Pintu',
    text: 'SELAMAT DATANG DI KELAS',
    context: 'Pintu Masuk TK',
    category: 'Sekolah',
    imageUrl: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=600&q=80',
  },
];
